import {
    DiagramWidgetData,
    DocumentWidgetData,
    DvCanvas,
    LinkLineStyle,
    MarkdownWidgetData,
    ReactionWidgetData,
    ShapeType,
    ShapeWidgetData,
    StickyNoteData,
    TableWidgetData,
    TaskWidgetData,
    TextWidgetData,
    Widget,
    WidgetLink,
    WidgetType
} from '@dv5/domain'

const LINK_ARROWS: Record<LinkLineStyle, string> = {
    solid: '-->',
    dashed: '..>',
    dotted: '..',
}

const SHAPE_STEREOTYPES: Partial<Record<ShapeType, string>> = {
    [ShapeType.TRIANGLE]: 'triangle',
    [ShapeType.DIAMOND]: 'diamond',
    [ShapeType.PROCESS]: 'process',
}

interface NodeKind {
    kind: string
    stereotype: string | null
}

const getShapeNodeKind = (shapeType: ShapeType): string => {
    switch (shapeType) {
        case ShapeType.DATABASE:
            return 'database'
        case ShapeType.QUEUE:
            return 'queue'
        case ShapeType.OVAL:
            return 'circle'
        default:
            return 'rectangle'
    }
}

const getWidgetNodeKind = (widget: Widget): NodeKind => {
    if (widget.type === WidgetType.SHAPE) {
        const shapeData = widget.data as ShapeWidgetData
        return {
            kind: getShapeNodeKind(shapeData.shapeType),
            stereotype: SHAPE_STEREOTYPES[shapeData.shapeType] || null,
        }
    }
    return {kind: 'rectangle', stereotype: null}
}

const getWidgetLabel = (widget: Widget): string => {
    switch (widget.type) {
        case WidgetType.STICKY_NOTE:
            return (widget.data as StickyNoteData).text || 'no text'
        case WidgetType.TEXT_WIDGET:
            return (widget.data as TextWidgetData).text || 'no text'
        case WidgetType.MARKDOWN:
            return (widget.data as MarkdownWidgetData).text || 'no text'
        case WidgetType.DIAGRAM:
            return (widget.data as DiagramWidgetData).text || 'no text'
        case WidgetType.TABLE:
            return (widget.data as TableWidgetData).text || 'no text'
        case WidgetType.DOCUMENT:
            return ((widget.data as DocumentWidgetData).title || 'Document') +
                (widget.targetAvailable === false ? ' (Broken link)' : '')
        case WidgetType.TASK:
            return ((widget.data as TaskWidgetData).title || 'Task') +
                (widget.targetAvailable === false ? ' (Broken link)' : '')
        case WidgetType.REACTION:
            return (widget.data as ReactionWidgetData).reactionType || 'no text'
        case WidgetType.SHAPE:
            return (widget.data as ShapeWidgetData).text || 'no text'
        default:
            return ''
    }
}

const escapePlantUmlLabel = (label: string): string =>
    label
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\r?\n/g, '\\n')

const compareWidgets = (a: Widget, b: Widget): number => {
    const zDiff = a.zIndex - b.zIndex
    if (zDiff !== 0) {
        return zDiff
    }
    const yDiff = a.top - b.top
    if (yDiff !== 0) {
        return yDiff
    }
    return a.left - b.left
}

const createNodeLine = (widget: Widget, alias: string): string => {
    const {kind, stereotype} = getWidgetNodeKind(widget)
    const label = escapePlantUmlLabel(getWidgetLabel(widget))
    const stereotypePart = stereotype ? ` <<${stereotype}>>` : ''
    return `${kind} "${label}"${stereotypePart} as ${alias}`
}

export const exportCanvasToMarkdown = (
    canvas: DvCanvas,
    widgets: Array<Widget>,
    widgetLinks: Array<WidgetLink>,
): string => {
    const sortedWidgets = [...widgets].sort(compareWidgets)

    const aliasById = new Map<string, string>()
    const nodeLines: Array<string> = []
    sortedWidgets.forEach((widget, index) => {
        const alias = `w${index + 1}`
        aliasById.set(widget.id, alias)
        nodeLines.push(createNodeLine(widget, alias))
    })

    const linkLines: Array<string> = []
    widgetLinks.forEach(link => {
        const startAlias = aliasById.get(link.startWidgetId)
        const endAlias = aliasById.get(link.endWidgetId)
        if (startAlias && endAlias) {
            const lineStyle = (link.data?.lineStyle as LinkLineStyle) || 'solid'
            linkLines.push(`${startAlias} ${LINK_ARROWS[lineStyle]} ${endAlias}`)
        }
    })

    const diagramLines = ['@startuml', ...nodeLines]
    if (nodeLines.length > 0 && linkLines.length > 0) {
        diagramLines.push('')
    }
    diagramLines.push(...linkLines, '@enduml')

    const blocks: Array<string> = []
    if (canvas.title && canvas.title.length > 0) {
        blocks.push(`# ${canvas.title}`)
    }
    blocks.push(['```plantuml', ...diagramLines, '```'].join('\n'))
    return blocks.join('\n\n') + '\n'
}
